import type {
  Map as MapLibreMap,
  RasterSourceSpecification,
  StyleSpecification,
} from 'maplibre-gl';

// Same Esri tile endpoints the app used via Cesium's UrlTemplateImageryProvider —
// kept identical so no new API key/account is required by this migration.
const SATELLITE_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const NEUTRAL_ROAD_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';

const ESRI_ATTRIBUTION = 'Tiles © Esri';

export const SATELLITE_SOURCE_ID = 'esri-satellite';
export const NEUTRAL_ROAD_SOURCE_ID = 'esri-neutral-road';
export const SATELLITE_LAYER_ID = 'esri-satellite-layer';
export const NEUTRAL_ROAD_LAYER_ID = 'esri-neutral-road-layer';
export const BACKGROUND_LAYER_ID = 'basemap-background';

// Default fill used by sites with no usable satellite coverage — replaces
// Cesium's `viewer.scene.globe.baseColor` (the old app never set terrain, so
// this flat fill is the visual equivalent of "no imagery tiles at all").
export const NEUTRAL_BACKDROP_COLOR = '#d9d9d9';

// Esri's two raster services have very different real coverage per region,
// and both return the exact SAME "Map data not yet available" tile as a
// normal 200 response once you're past it for a given spot — not a 404, and
// not distinguishable from real data by status code, and the coverage gaps
// are PATCHY rather than a clean "below zoom X" cutoff (verified directly
// against the tile server: real imagery at one zoom, the identical
// placeholder a few tiles over at the SAME zoom). That rules out a per-site
// zoom cap as a full fix — it can only protect the one point it was
// calibrated against. Callers that hit this should prefer
// applyNeutralBackdrop (no raster tiles requested at all, so nothing can
// ever show "no data") over setBasemap('neutralRoad') wherever the
// neutral-road canvas layer's coverage for that customer's site is in
// doubt. These stay as the map's initial/interactive zoom ceiling for
// whichever basemap IS in use.
export const SATELLITE_MAX_ZOOM = 19;
export const NEUTRAL_ROAD_MAX_ZOOM = 16;

export const satelliteRasterSource: RasterSourceSpecification = {
  type: 'raster',
  tiles: [SATELLITE_TILE_URL],
  tileSize: 256,
  maxzoom: 19,
  attribution: ESRI_ATTRIBUTION,
};

export const neutralRoadRasterSource: RasterSourceSpecification = {
  type: 'raster',
  tiles: [NEUTRAL_ROAD_TILE_URL],
  tileSize: 256,
  maxzoom: 19,
  attribution: ESRI_ATTRIBUTION,
};

export type BasemapId = 'satellite' | 'neutralRoad';

// Builds a self-contained MapLibre style with both basemaps as sources but only
// `defaultBasemap` visible — used as the `mapStyle` for MapView, with the
// non-default layer's visibility flipped at runtime for the satellite/neutral-road
// toggle (replaces Cesium's imageryProviderViewModels base layer picker).
// Free, no-API-key glyph (font) source — required for any `symbol` layer's
// `text-field` to render at all. Without this, text-field labels silently
// don't draw (no error), which is why some early ports of Cesium label
// Entities to MapLibre symbol layers had to drop labels rather than port them.
const GLYPHS_URL = 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';

// Leaving `defaultBasemap` unset starts with BOTH raster layers hidden and a
// flat neutral-gray background instead — no tiles requested at all until a
// caller explicitly picks one via setBasemap/applyNeutralBackdrop. Most
// views (AssetMap included) don't pass a basemap prop and instead decide the
// real one imperatively, shortly after mount, once they know the customer's
// site treatment; showing satellite as an interim default meant every load
// briefly showed the wrong basemap (or a "no data" placeholder, if the
// camera's initial zoom already exceeded that basemap's real coverage
// before the real choice took over) for however long that decision took. A
// caller that DOES know its basemap up front (e.g. a view that always shows
// neutral-road and never toggles) can still pass it to skip the neutral
// flash entirely.
export const buildBasemapStyle = (
  defaultBasemap?: BasemapId
): StyleSpecification => ({
  version: 8,
  glyphs: GLYPHS_URL,
  sources: {
    [SATELLITE_SOURCE_ID]: satelliteRasterSource,
    [NEUTRAL_ROAD_SOURCE_ID]: neutralRoadRasterSource,
  },
  layers: [
    {
      id: BACKGROUND_LAYER_ID,
      type: 'background',
      paint: {
        'background-color': NEUTRAL_BACKDROP_COLOR,
        'background-opacity': defaultBasemap ? 0 : 1,
      },
    },
    {
      id: SATELLITE_LAYER_ID,
      type: 'raster',
      source: SATELLITE_SOURCE_ID,
      layout: {
        visibility: defaultBasemap === 'satellite' ? 'visible' : 'none',
      },
    },
    {
      id: NEUTRAL_ROAD_LAYER_ID,
      type: 'raster',
      source: NEUTRAL_ROAD_SOURCE_ID,
      layout: {
        visibility: defaultBasemap === 'neutralRoad' ? 'visible' : 'none',
      },
    },
  ],
});

// Hides both Esri raster layers and shows a plain solid-color background —
// used for sites with no usable satellite coverage (replaces
// `viewer.scene.globe.baseColor`) or a "neutral backdrop only" customer.
export const applyNeutralBackdrop = (
  map: MapLibreMap,
  color: string = NEUTRAL_BACKDROP_COLOR
) => {
  map.setLayoutProperty(SATELLITE_LAYER_ID, 'visibility', 'none');
  map.setLayoutProperty(NEUTRAL_ROAD_LAYER_ID, 'visibility', 'none');
  map.setPaintProperty(BACKGROUND_LAYER_ID, 'background-color', color);
  map.setPaintProperty(BACKGROUND_LAYER_ID, 'background-opacity', 1);
};

// Shows one of the two Esri raster basemaps and clears any neutral backdrop.
// Also caps the map's interactive max zoom to that basemap's static ceiling
// (see SATELLITE_MAX_ZOOM/NEUTRAL_ROAD_MAX_ZOOM above) so zooming in can't
// reach the deepest, most-likely-to-be-missing tiles — real coverage gaps
// can still show up before that ceiling since they're patchy, not a clean
// cutoff; prefer applyNeutralBackdrop over 'neutralRoad' wherever that
// layer's coverage for the customer's site isn't known to be solid.
export const setBasemap = (map: MapLibreMap, basemap: BasemapId) => {
  map.setLayoutProperty(
    SATELLITE_LAYER_ID,
    'visibility',
    basemap === 'satellite' ? 'visible' : 'none'
  );
  map.setLayoutProperty(
    NEUTRAL_ROAD_LAYER_ID,
    'visibility',
    basemap === 'neutralRoad' ? 'visible' : 'none'
  );
  map.setPaintProperty(BACKGROUND_LAYER_ID, 'background-opacity', 0);
  map.setMaxZoom(
    basemap === 'neutralRoad' ? NEUTRAL_ROAD_MAX_ZOOM : SATELLITE_MAX_ZOOM
  );
};

type GeoJsonCoordTransform = (
  x: number,
  y: number
) => { lat: number; lon: number } | null;

// Recurses into GeoJSON coordinate arrays (Point/LineString/Polygon/Multi*)
// and remaps every [x, y, ...] leaf pair through `transform` — used to bring
// a GeoJSON file authored in a local/site grid (e.g. converted from a DXF)
// into WGS84 lon/lat so it can be rendered on the map.
const transformCoordinates = (
  coords: unknown,
  transform: GeoJsonCoordTransform
): unknown => {
  if (
    Array.isArray(coords) &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number'
  ) {
    const point = transform(coords[0], coords[1]);
    return point ? [point.lon, point.lat] : null;
  }
  if (Array.isArray(coords)) {
    return coords
      .map((c) => transformCoordinates(c, transform))
      .filter((c) => c != null);
  }
  return coords;
};

export interface LocalGeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: string; coordinates: unknown };
    properties?: Record<string, unknown> | null;
  }>;
}

// Recurses into GeoJSON coordinate arrays and averages every [lon, lat] leaf
// pair — used to center the camera on an arbitrary imported GeoJSON file
// without needing a full bbox-fit dependency.
const collectLonLatPairs = (coords: unknown, out: Array<[number, number]>) => {
  if (
    Array.isArray(coords) &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number'
  ) {
    out.push([coords[0], coords[1]]);
    return;
  }
  if (Array.isArray(coords)) {
    coords.forEach((c) => {
      collectLonLatPairs(c, out);
    });
  }
};

export const geoJsonCentroid = (
  geojson: LocalGeoJsonFeatureCollection
): { lat: number; lon: number } | null => {
  const pairs: Array<[number, number]> = [];
  geojson.features.forEach((feature) => {
    collectLonLatPairs(feature.geometry.coordinates, pairs);
  });
  if (pairs.length === 0) return null;
  const avgLon = pairs.reduce((s, [lon]) => s + lon, 0) / pairs.length;
  const avgLat = pairs.reduce((s, [, lat]) => s + lat, 0) / pairs.length;
  return { lat: avgLat, lon: avgLon };
};

export const transformGeoJsonToLatLon = (
  geojson: LocalGeoJsonFeatureCollection,
  transform: GeoJsonCoordTransform
): LocalGeoJsonFeatureCollection => ({
  ...geojson,
  features: geojson.features
    .map((feature) => ({
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: transformCoordinates(
          feature.geometry.coordinates,
          transform
        ),
      },
    }))
    .filter((feature) => feature.geometry.coordinates != null),
});

export interface LonLatPoint {
  lon: number;
  lat: number;
}

export interface PaddedLonLatBounds {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

// Padded bounding box around a set of points — used to build a MapLibre
// `maxBounds` that locks the camera to a site/trip's own extent instead of
// letting the user pan or zoom out into empty space beyond it.
export const getPaddedLonLatBounds = (
  points: LonLatPoint[],
  paddingFactor = 0.2,
  minPaddingDegrees = 0.002
): PaddedLonLatBounds | null => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  points.forEach(({ lon, lat }) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  });
  if (!Number.isFinite(minLat) || !Number.isFinite(minLon)) return null;
  const latPad = Math.max((maxLat - minLat) * paddingFactor, minPaddingDegrees);
  const lonPad = Math.max((maxLon - minLon) * paddingFactor, minPaddingDegrees);
  return {
    minLat: minLat - latPad,
    maxLat: maxLat + latPad,
    minLon: minLon - lonPad,
    maxLon: maxLon + lonPad,
  };
};

// [west, south, east, north] — the tuple form MapLibre's `maxBounds` /
// `setMaxBounds` accepts.
export const boundsToMaxBoundsTuple = (
  bounds: PaddedLonLatBounds
): [number, number, number, number] => [
  bounds.minLon,
  bounds.minLat,
  bounds.maxLon,
  bounds.maxLat,
];
